import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";

import {
  Space,
  Button,
  Drawer,
  Progress,
  Card,
} from '@lockerpm/design';

import {
  ExpandOutlined
} from '@ant-design/icons';

import formComponents from '../form';

import global from '../../config/global';
import secretServices from '../../services/secret';
import environmentServices from '../../services/environment';
import workspaceServices from '../../services/workspace';
import projectServices from '../../services/project';
import commonServices from '../../services/common';
import storeActions from '../../store/actions';

import { getProjectLoadedPercent, timeFromNow, convertDateTime } from '../../utils/common';

function ImportProgress(props) {
  const { ImportExportProgressForm } = formComponents;
  const {
    progress = {},
  } = props
  const { t } = useTranslation();
  const selectedWorkspace = useSelector((state) => state.workspace.selectedWorkspace);

  const [callingAPI, setCallingAPI] = useState(false);
  const [imported, setImported] = useState(false);
  const [selectedProjects, setSelectedProjects] = useState([...(progress?.projects || [])])
  const [visible, setVisible] = useState(true);
  const [finished, setFinished] = useState(false);

  useEffect(() => {
    handleImport();
  }, [])

  const totalProjects = useMemo(() => {
    return selectedProjects.length
  }, [selectedProjects])

  const totalLoadedProjects = useMemo(() => {
    return selectedProjects.filter((p) => p.percent == 100).length;
  }, [selectedProjects])

  const percent = useMemo(() => {
    const totalSecrets = selectedProjects.map((p) => p.total_secret).reduce((a, b) => a + b, 0);
    const totalEnvironments = selectedProjects.map((p) => p.total_environment).reduce((a, b) => a + b, 0);

    const totalLoadedSecrets = selectedProjects.map((p) => p.pSecrets).flat().length;
    const totalLoadedEnvironments = selectedProjects.map((p) => p.pEnvironments).flat().length;

    return Number(((totalLoadedSecrets + totalLoadedEnvironments) /  (totalSecrets + totalEnvironments) * 100).toFixed(0))
  }, [totalProjects, selectedProjects])

  const importData = async () => {
    const allRequests = selectedProjects.map(async (p) => {
      const projectPayload = await projectServices.get_payload({ name: p.name, description: p.description }, progress.workspaceId);
      const projectResponse = await projectServices.create(projectPayload);
      await commonServices.sync_profile();

      p.id = projectResponse.id;
      p.secrets = (p.secrets || []).map((s) => ({ ...s, organizationId: projectResponse.id })).reverse();
      p.environments = (p.environments || []).map((s) => ({ ...s, organizationId: projectResponse.id })).reverse();

      let environments = []
      let projectPercent = getProjectLoadedPercent(p);
      p.percent = projectPercent;
      const sProjects = selectedProjects.map((project) => {
        if (p.id === project.id) {
          return {
            ...project,
            percent: projectPercent,
          }
        }
        return project
      })
      setSelectedProjects(sProjects);
      while (environments.length < p.total_environment) {
        const envs = p.environments.slice(environments.length, environments.length + global.constants.IMPORT_PAGE_SIZE)
        const environmentsPayload = await Promise.all(envs.map(async env => ({
          ...await (environmentServices.get_payload(env, env.organizationId)),
          project_id: env.organizationId
        })))
        await workspaceServices.import_data(progress.workspaceId, { secrets: [], environments: environmentsPayload })
        environments = [...environments, ...envs]
        p.pEnvironments = environments;
        projectPercent = getProjectLoadedPercent(p)
        p.percent = projectPercent;
        const sProjects = selectedProjects.map((project) => {
          if (p.id === project.id) {
            return {
              ...project,
              percent: projectPercent,
              pEnvironments: environments,
            }
          }
          return project
        })
        setSelectedProjects(sProjects);
      }
      let secrets = [];
      while (secrets.length < p.total_secret) {
        const secs = p.secrets.slice(secrets.length, secrets.length + global.constants.IMPORT_PAGE_SIZE);
        const secretsPayload = await Promise.all(secs.map(async s => ({
          ...(await secretServices.get_payload(s, s.organizationId)),
          project_id: s.organizationId,
        })))
        await workspaceServices.import_data(progress.workspaceId, { secrets: secretsPayload, environments: [] })
        secrets = [...secrets, ...secs]
        p.pSecrets = secrets;
        projectPercent = getProjectLoadedPercent(p);
        p.percent = projectPercent;
        const sProjects = selectedProjects.map((project) => {
          if (p.id === project.id) {
            return {
              ...project,
              percent: projectPercent,
              pEnvironments: environments,
              pSecrets: secrets
            }
          }
          return project
        })
        setSelectedProjects(sProjects);
      }
    })
    await Promise.all(allRequests);
  }

  const handleImport = async () => {
    setCallingAPI(true);
    await importData();
    global.pushSuccess(t('notification.success.import_export.imported'))
    global.store.dispatch(storeActions.updateIsFetchProjects(true))
    setImported(true);
    setCallingAPI(false);
    setTimeout(() => {
      setFinished(true);
    }, 10000);
  }

  return (
    <>
      {
        !finished && selectedWorkspace.id == progress.workspaceId && <div className={props.className}>
          <Card
            className='mt-2 border-primary'
            key={progress.id}
            bodyStyle={{ padding: '6px 12px' }}
          >
            <div className='flex items-center justify-between'>
              <p className='font-semibold'>
                {t('import_export.importing', { project_member: `${totalLoadedProjects}/${totalProjects}` })}
              </p>
              <div className='text-primary cursor-pointer' onClick={() => setVisible(true)}>
                <ExpandOutlined />
              </div>
            </div>
            <Progress className='m-0' percent={percent} />
            <p className='text-xs'>{timeFromNow(progress.createdAt / 1000)}</p>
          </Card>
          <Drawer
            title={t('import_export.import')}
            placement="right"
            onClose={() => setVisible(false)}
            open={visible}
            width={480}
            closable={false}
            footer={
              <Space className='flex items-center justify-end'>
                {
                  !imported && <Button
                    type="primary"
                    loading={callingAPI}
                    onClick={() => {}}
                  >
                    { t('button.import') } 
                  </Button>
                }
                {
                  imported && <Button
                    disabled={callingAPI}
                    onClick={() => setFinished(true)}
                  >
                    {t('button.close')}
                  </Button>
                }
              </Space>
            }
          >
            <p className='font-semibold'>
              {t('common.created_time')}: {convertDateTime(progress.createdAt / 1000, 'hh:mm:ss DD-MM-YYYY')}
            </p>
            <ImportExportProgressForm
              selectedProjects={selectedProjects}
            />
          </Drawer>
        </div>
      }
    </>
  );
}

export default ImportProgress;
